import { useCallback, useEffect, useRef, useState } from 'react'

// One playback hook for narration and voice memos, in the composer and the
// reader. Real sources play through an <audio> element it owns; `demo:`
// sources (the local demo data has no audio files) run a silent clock at the
// given duration so sync and progress still demo offline.
//
// Only one recording plays at a time: starting one pauses whichever other
// player was playing (narration vs. a voice memo, or two memos).

export interface AudioPlayer {
  playing: boolean
  /** Seconds. */
  time: number
  /** Seconds; the known duration until metadata arrives. */
  duration: number
  rate: number
  error: string | null
  play(): void
  pause(): void
  toggle(): void
  seek(seconds: number): void
  setRate(rate: number): void
  /**
   * The exact playback position right now. `time` is React state that real
   * audio only updates a few times a second; stamping paragraphs reads this.
   */
  currentTime?(): number
}

export const PLAYBACK_RATES = [1, 1.25, 1.5, 2]

export function isDemoAudio(src: string | null | undefined): boolean {
  return !!src && src.startsWith('demo:')
}

/** Anything that makes sound and can be told to stop (see claim/release). */
export interface PlaybackOwner {
  pause(): void
}
type Owner = PlaybackOwner
/** The player currently making sound, if any. */
let nowPlaying: Owner | null = null

/** `owner` is about to play: whichever other player was playing pauses. */
export function claim(owner: Owner) {
  if (nowPlaying && nowPlaying !== owner) nowPlaying.pause()
  nowPlaying = owner
}

/** `owner` stopped making sound. */
export function release(owner: Owner) {
  if (nowPlaying === owner) nowPlaying = null
}

export function useAudioPlayer(src: string | null | undefined, knownDuration = 0): AudioPlayer {
  const [playing, setPlaying] = useState(false)
  const [time, setTime] = useState(0)
  const [duration, setDuration] = useState(knownDuration)
  const [rate, setRateState] = useState(1)
  const [error, setError] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const demo = isDemoAudio(src)
  // Demo clock state lives in refs so the ticker reads fresh values.
  const clock = useRef({ time: 0, rate: 1, last: 0, raf: 0 })
  const knownRef = useRef(knownDuration)
  knownRef.current = knownDuration
  const owner = useRef<Owner>({ pause: () => {} }).current

  useEffect(() => setDuration(knownDuration), [knownDuration])

  // Real audio element lifecycle.
  useEffect(() => {
    setPlaying(false)
    setTime(0)
    setError(null)
    // A new source starts from what the caller knows, not the last file's metadata.
    setDuration(knownRef.current)
    clock.current.time = 0
    if (!src || demo || typeof Audio === 'undefined') return
    const el = new Audio()
    el.preload = 'metadata'
    el.src = src
    el.defaultPlaybackRate = clock.current.rate
    el.playbackRate = clock.current.rate
    audioRef.current = el
    const onTime = () => setTime(el.currentTime)
    const onMeta = () => {
      if (Number.isFinite(el.duration) && el.duration > 0) setDuration(el.duration)
    }
    const onEnd = () => {
      setPlaying(false)
      release(owner)
    }
    const onPlay = () => {
      setPlaying(true)
      setError(null)
    }
    const onPause = () => setPlaying(false)
    const onError = () => {
      setPlaying(false)
      release(owner)
      setError('This recording could not be played.')
    }
    el.addEventListener('timeupdate', onTime)
    el.addEventListener('loadedmetadata', onMeta)
    el.addEventListener('durationchange', onMeta)
    el.addEventListener('ended', onEnd)
    el.addEventListener('play', onPlay)
    el.addEventListener('pause', onPause)
    el.addEventListener('error', onError)
    return () => {
      el.pause()
      el.removeEventListener('timeupdate', onTime)
      el.removeEventListener('loadedmetadata', onMeta)
      el.removeEventListener('durationchange', onMeta)
      el.removeEventListener('ended', onEnd)
      el.removeEventListener('play', onPlay)
      el.removeEventListener('pause', onPause)
      el.removeEventListener('error', onError)
      el.removeAttribute('src')
      audioRef.current = null
      release(owner)
    }
  }, [src, demo, owner])

  // Demo clock ticker.
  useEffect(() => {
    if (!demo || !playing) return
    const c = clock.current
    c.last = performance.now()
    const tick = () => {
      // performance.now(), not the frame's timestamp: some engines (jsdom) time those from another origin.
      const now = performance.now()
      c.time = Math.min(duration, c.time + (Math.max(0, now - c.last) / 1000) * c.rate)
      c.last = now
      setTime(c.time)
      if (c.time >= duration) {
        setPlaying(false)
        release(owner)
        return
      }
      c.raf = requestAnimationFrame(tick)
    }
    c.raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(c.raf)
  }, [demo, playing, duration, owner])

  useEffect(() => () => release(owner), [owner])

  const play = useCallback(() => {
    if (!src) return
    claim(owner)
    if (demo) {
      if (clock.current.time >= duration) clock.current.time = 0
      setError(null)
      setPlaying(true)
      return
    }
    const el = audioRef.current
    if (!el) return
    // Older engines return undefined rather than a promise.
    const started = el.play() as Promise<void> | undefined
    started?.catch?.(() => {
      release(owner)
      setError('Playback was blocked. Tap play again.')
    })
  }, [src, demo, duration, owner])

  const pause = useCallback(() => {
    release(owner)
    if (demo) setPlaying(false)
    else audioRef.current?.pause()
  }, [demo, owner])
  owner.pause = pause

  const seek = useCallback(
    (seconds: number) => {
      const t = Math.max(0, Math.min(seconds, duration || seconds))
      if (demo) clock.current.time = t
      else if (audioRef.current) audioRef.current.currentTime = t
      setTime(t)
    },
    [demo, duration],
  )

  const setRate = useCallback((r: number) => {
    setRateState(r)
    clock.current.rate = r
    if (audioRef.current) {
      audioRef.current.defaultPlaybackRate = r
      audioRef.current.playbackRate = r
    }
  }, [])

  const currentTime = useCallback(
    () => (demo ? clock.current.time : (audioRef.current?.currentTime ?? clock.current.time)),
    [demo],
  )

  return {
    playing,
    time,
    duration,
    rate,
    error,
    play,
    pause,
    toggle: () => (playing ? pause() : play()),
    seek,
    setRate,
    currentTime,
  }
}
